import {
  BadgeCheck,
  BarChart3,
  ClipboardCheck,
  Image,
  LayoutDashboard,
  type LucideIcon,
  PlusCircle,
  Receipt,
  Scale,
  ShieldCheck,
} from "lucide-react";

/**
 * Catálogo de perfis de acesso do Portal do Gerador — GR-637 (Onda 0).
 *
 * Cada perfil é um Group do realm `generator` no Keycloak, e as capacidades
 * listadas são as client roles que aquele grupo concede em `portal-gerador`.
 * A fonte de verdade é `management/src/generator.tf` no repositório keycloak;
 * este arquivo é o espelho para exibição, e os dois precisam andar juntos.
 *
 * A `key` é o que trafega no `add`/`remove` de `PUT /users/{pk}/profiles` —
 * inglês, kebab-case, sem acento. O `label` é só rótulo de tela.
 */

export const PERMISSION_PROFILE_KEYS = [
  "default",
  "enterprise",
  "add-on-request",
  "add-on-approval",
  "financial",
  "measurement-approval",
  "legal",
  "data",
  "admin",
] as const;

export type PermissionProfileKey = (typeof PERMISSION_PROFILE_KEYS)[number];

export interface PermissionCapability {
  /** Tela ou área do Portal onde a capacidade vale. */
  area: string;
  /** O que a pessoa consegue fazer ali. */
  action: string;
  /**
   * A permissão existe no Keycloak, mas a funcionalidade ainda não foi
   * construída no Portal. Marcar em tela evita o admin conceder o acesso e
   * ficar procurando uma tela que não existe.
   */
  planned?: boolean;
}

export interface PermissionProfile {
  key: PermissionProfileKey;
  label: string;
  /** Uma linha, para quem só bate o olho na lista. */
  summary: string;
  capabilities: PermissionCapability[];
  icon: LucideIcon;
  /** Agrupamento temático da tela — só organização visual, não existe no Keycloak. */
  section: PermissionProfileSection;
}

export type PermissionProfileSection =
  | "Acesso base"
  | "Coleta sob demanda"
  | "Financeiro"
  | "Análise e contrato"
  | "Gestão da empresa";

/** Ordem de exibição das seções na tela. */
export const PERMISSION_PROFILE_SECTIONS: PermissionProfileSection[] = [
  "Acesso base",
  "Coleta sob demanda",
  "Financeiro",
  "Análise e contrato",
  "Gestão da empresa",
];

/** Perfil que concede a gestão de usuários — define `permission_role: "admin"`. */
export const ADMIN_PROFILE_KEY: PermissionProfileKey = "admin";

/** Perfil de acesso base, sugerido em toda criação de usuário. */
export const DEFAULT_PROFILE_KEY: PermissionProfileKey = "default";

/**
 * Perfis que todo usuário de gerador tem, sempre. Vêm marcados e não podem ser
 * desmarcados na tela.
 *
 * `default` é o acesso base do Portal (painel, coletas, indicadores, suporte,
 * Meu Perfil, Central de Ajuda, termos). Sem ele a pessoa entra e não vê tela
 * nenhuma — e, desde que o menu lateral passou a filtrar por permissão, isso
 * vira literalmente uma barra lateral em branco. Não é uma escolha que faça
 * sentido oferecer ao admin.
 */
export const MANDATORY_PROFILE_KEYS: readonly PermissionProfileKey[] = [
  DEFAULT_PROFILE_KEY,
];

/** `true` quando o perfil não pode ser desmarcado. */
export function isMandatoryProfile(key: string): boolean {
  return (MANDATORY_PROFILE_KEYS as readonly string[]).includes(key);
}

export const PERMISSION_PROFILES: PermissionProfile[] = [
  {
    key: "default",
    section: "Acesso base",
    label: "Padrão",
    summary: "Acesso base do Portal — painel, coletas, indicadores e suporte.",
    icon: LayoutDashboard,
    capabilities: [
      { area: "Painel", action: "Visualizar e navegar" },
      {
        area: "Indicadores Ambientais",
        action: "Visualizar, incluindo certificados de compensação quando contratados",
      },
      {
        area: "Agenda de coletas",
        action: "Visualizar, baixar dados em CSV e MTRs em massa",
      },
      {
        area: "Histórico de coletas",
        action: "Visualizar, baixar dados em CSV e MTRs em massa",
      },
      {
        area: "Coleta sob demanda",
        action: "Visualizar e solicitar coleta prevista em contrato",
      },
      { area: "Suporte", action: "Visualizar e abrir chamado" },
      { area: "Meu Perfil", action: "Visualizar e editar, exceto o e-mail" },
      { area: "Central de Ajuda", action: "Visualizar e navegar" },
      { area: "CSAT", action: "Visualizar e responder o formulário" },
      { area: "Termos e condições", action: "Visualizar e navegar" },
    ],
  },
  {
    key: "enterprise",
    section: "Acesso base",
    label: "Visualização de evidências",
    summary: "Soma ao Padrão a visualização das evidências de coleta.",
    icon: Image,
    capabilities: [
      {
        area: "Agenda e Histórico de coletas",
        action: "Visualizar evidências de coleta",
      },
    ],
  },
  {
    key: "add-on-request",
    section: "Coleta sob demanda",
    label: "Solicitar Add-on",
    summary: "Solicita serviços fora do contrato.",
    icon: PlusCircle,
    capabilities: [
      {
        area: "Coleta sob demanda",
        action: "Solicitar coleta não prevista em contrato",
      },
      {
        area: "Indicadores Ambientais",
        action: "Contratar emissão de certificado de compensação de carbono",
        planned: true,
      },
    ],
  },
  {
    key: "add-on-approval",
    section: "Coleta sob demanda",
    label: "Aprovação Add-on",
    summary: "Decide sobre as solicitações de coleta sob demanda.",
    icon: BadgeCheck,
    capabilities: [
      { area: "Coleta sob demanda", action: "Cancelar solicitação" },
      { area: "Coleta sob demanda", action: "Aprovar ou recusar a precificação" },
    ],
  },
  {
    key: "financial",
    section: "Financeiro",
    label: "Financeiro",
    summary: "Faturas, boleto e nota fiscal.",
    icon: Receipt,
    capabilities: [
      {
        area: "Financeiro",
        action: "Visualizar faturas e baixar boleto e nota fiscal",
      },
      {
        area: "Financeiro",
        action: "Editar prazo de vencimento",
        planned: true,
      },
    ],
  },
  {
    key: "measurement-approval",
    section: "Financeiro",
    label: "Aprovação medição",
    summary: "Contesta ou aprova a medição que gera a fatura.",
    icon: ClipboardCheck,
    capabilities: [
      {
        area: "Financeiro",
        action: "Contestar ou aprovar medição",
      },
    ],
  },
  {
    key: "legal",
    section: "Análise e contrato",
    label: "Jurídico",
    summary: "Contrato com a Musa.",
    icon: Scale,
    capabilities: [
      {
        area: "Contrato Musa",
        action: "Visualizar e consultar o PDF do contrato",
        planned: true,
      },
    ],
  },
  {
    key: "data",
    section: "Análise e contrato",
    label: "Dados",
    summary: "Dashboards analíticos de performance e compliance.",
    icon: BarChart3,
    capabilities: [
      {
        area: "Dashboard de performance",
        action: "Visualizar e navegar",
        planned: true,
      },
      {
        area: "Dashboard de compliance",
        action: "Visualizar e navegar",
        planned: true,
      },
    ],
  },
  {
    key: "admin",
    section: "Gestão da empresa",
    label: "Admin",
    summary: "Gestão da própria empresa, incluindo os usuários.",
    icon: ShieldCheck,
    capabilities: [
      {
        area: "Minha empresa",
        action: "Gerenciar usuários — criar, redefinir senha e remover",
      },
      {
        // O grupo `admin` concede `dispute-measurement` no `generator.tf`:
        // decidir sobre a fatura é do administrador da conta. O perfil
        // "Aprovação medição" continua existindo para dar a MESMA capacidade a
        // quem não é admin — por isso a linha aparece nos dois cards.
        area: "Financeiro",
        action: "Contestar ou aprovar medição",
      },
      {
        area: "Minha empresa",
        action: "Editar o logo do gerador (whitelabel)",
        planned: true,
      },
      {
        area: "Minha empresa",
        action: "Criar e editar a categorização de unidades e lojas",
        planned: true,
      },
    ],
  },
];

/**
 * O que a tela de seleção OFERTA: os perfis com ao menos uma capacidade
 * entregue, já sem as capacidades futuras.
 *
 * Anunciar ao admin um acesso que ele não consegue exercer não ajuda — ele
 * concede e a pessoa vai procurar uma tela que não existe. Perfil que fica sem
 * capacidade nenhuma sai inteiro, em vez de virar card com "0 permissões".
 *
 * Derivado, e não um recorte no catálogo, de propósito:
 *
 * - `PERMISSION_PROFILES` segue espelhando `generator.tf`, que é o que o
 *   docblock deste arquivo promete;
 * - `PermissionProfileKey` não encolhe, então `diffProfiles` e o `PUT` seguem
 *   aceitando as 9 chaves;
 * - `getPermissionProfileLabel` continua resolvendo `legal`/`data` pelo rótulo
 *   certo para quem já pertence a eles;
 * - o mesmo código serve às branches de `main` e `staging`: quem decide é o
 *   `planned` de cada capacidade, e ele já difere entre elas.
 *
 * Entregar uma tela é remover o `planned: true` da capacidade — ela volta a ser
 * ofertada sozinha, sem mexer em lista nenhuma.
 */
export const OFFERED_PERMISSION_PROFILES: PermissionProfile[] =
  PERMISSION_PROFILES.map((profile) => ({
    ...profile,
    capabilities: profile.capabilities.filter((c) => !c.planned),
  })).filter((profile) => profile.capabilities.length > 0);

const OFFERED_KEYS = new Set<string>(
  OFFERED_PERMISSION_PROFILES.map((profile) => profile.key),
);

/**
 * `true` quando o perfil aparece como checkbox na tela.
 *
 * O complemento NÃO é "não existe": um perfil do catálogo sem tela entregue
 * (`legal`, `data`) não é ofertado, mas quem já pertence a ele tem acesso real
 * e precisa vê-lo — em somente leitura, como os grupos antigos.
 */
export function isOfferedProfile(key: string): boolean {
  return OFFERED_KEYS.has(key);
}

/**
 * `true` quando NENHUMA capacidade do perfil existe no Portal ainda — o grupo
 * pode ser concedido, mas hoje não libera tela nenhuma. A tela sinaliza isso
 * no card, não só dentro da lista expandida.
 */
export function isProfileFullyPlanned(profile: PermissionProfile): boolean {
  return profile.capabilities.every((capability) => capability.planned);
}

const PROFILE_BY_KEY = new Map<string, PermissionProfile>(
  PERMISSION_PROFILES.map((profile) => [profile.key, profile]),
);

export function getPermissionProfile(key: string): PermissionProfile | undefined {
  return PROFILE_BY_KEY.get(key);
}

/**
 * Rótulo de exibição de um grupo. Grupos legados criados por gerador
 * (`Generator_3869` e afins) não estão no catálogo e continuam sendo
 * exibidos pelo próprio nome — some-los da tela esconderia acesso real.
 */
export function getPermissionProfileLabel(key: string): string {
  return PROFILE_BY_KEY.get(key)?.label ?? key;
}

/** `true` quando a chave não pertence ao catálogo GR-637. */
export function isLegacyProfile(key: string): boolean {
  return !PROFILE_BY_KEY.has(key);
}

/**
 * Diff declarativo → incremental, no formato que `PUT /users/{pk}/profiles`
 * espera. A ordem de `add`/`remove` é estável (segue o catálogo, com os
 * legados no fim) para o payload ser determinístico em teste.
 */
/** Ordena pelas chaves do catálogo; o que não é do catálogo (legado) vai no fim. */
function orderByCatalog(keys: string[]): string[] {
  const catalogIndex = new Map<string, number>(
    PERMISSION_PROFILE_KEYS.map((key, index) => [key, index]),
  );
  return [...keys].sort(
    (a, b) =>
      (catalogIndex.get(a) ?? Number.MAX_SAFE_INTEGER) -
        (catalogIndex.get(b) ?? Number.MAX_SAFE_INTEGER) || a.localeCompare(b),
  );
}

/**
 * Aplica um clique de checkbox à seleção de perfis.
 *
 * Desmarcar um perfil obrigatório é no-op — a tela já manda o checkbox
 * `disabled`, mas a regra mora aqui para valer também no caminho programático
 * (teclado em navegador que ignore o disabled, refactor que perca a prop).
 *
 * Devolve a MESMA referência quando nada muda, para o chamador poder pular o
 * `onChange`.
 */
export function toggleProfile(
  value: string[],
  key: string,
  checked: boolean,
): string[] {
  if (!checked) {
    if (isMandatoryProfile(key)) return value;
    return value.includes(key) ? value.filter((item) => item !== key) : value;
  }
  return value.includes(key) ? value : [...value, key];
}

/**
 * A seleção com os perfis obrigatórios garantidos. Preserva o que já estava,
 * inclusive grupo legado fora do catálogo.
 */
export function withMandatoryProfiles(keys: string[]): string[] {
  const present = new Set(keys);
  const faltando = MANDATORY_PROFILE_KEYS.filter((key) => !present.has(key));
  return faltando.length === 0 ? keys : orderByCatalog([...keys, ...faltando]);
}

/**
 * O que o `PUT /users/{pk}/profiles` precisa aplicar para sair de `current` e
 * chegar em `next`.
 *
 * Ponto único onde a obrigatoriedade é imposta no que vai para a API: `next` é
 * normalizado antes do cálculo, então `remove` nunca contém um perfil
 * obrigatório e `add` o inclui quando o servidor ainda não tem. Fazer isso aqui
 * — e não em cada chamador — é o que impede um caminho novo de gravar um
 * usuário sem acesso base.
 */
export function diffProfiles(
  current: string[],
  next: string[],
): { add: string[]; remove: string[] } {
  const effectiveNext = withMandatoryProfiles(next);
  const currentSet = new Set(current);
  const nextSet = new Set(effectiveNext);

  return {
    add: orderByCatalog(effectiveNext.filter((key) => !currentSet.has(key))),
    remove: orderByCatalog(current.filter((key) => !nextSet.has(key))),
  };
}
